import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { EllipsisHorizontalIcon } from '@heroicons/react/24/solid'
import { DataTableBodyCell } from './DataTableCell'
import { DataTableEditableCell } from './DataTableEditableCell'
import type { ColumnDefinition, DataTableItem, RowAction, SelectionConfig, EditingCell } from './types'

interface DataTableRowProps<T extends DataTableItem> {
  row: T
  columns: ColumnDefinition<T>[]
  visibleColumns: string[]
  isSelected: boolean
  isNextSelected: boolean
  selection?: SelectionConfig<T>
  rowActions?: RowAction<T>[]
  isScrolledX?: boolean
  editingCell?: EditingCell | null
  onStartEdit?: (cell: EditingCell) => void
  onSaveEdit?: (rowId: string | number, columnId: string, value: string) => void
  onCancelEdit?: () => void
  disableRowHover?: boolean
  hasSpacerColumn?: boolean
}

export function DataTableRow<T extends DataTableItem>({
  row,
  columns,
  visibleColumns,
  isSelected,
  isNextSelected,
  selection,
  rowActions,
  isScrolledX = false,
  editingCell,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  disableRowHover = false,
  hasSpacerColumn = false
}: DataTableRowProps<T>) {
  const bottomBorderClass = isSelected || isNextSelected ? 'border-table-grid-selected' : 'border-table-grid'

  // Background class for cells that don't use DataTableBodyCell (actions, spacer)
  const getCellBgClass = () => {
    if (isSelected) return 'bg-surface-selected'
    if (disableRowHover) return 'bg-table'
    return 'bg-table group-hover:bg-table-row-hover'
  }
  const cellBgClass = getCellBgClass()

  return (
    <tr
      data-row-id={row.id}
      data-state={isSelected ? 'selected' : undefined}
      className={`border-b group ${bottomBorderClass}`}
    >
      {columns
        .filter(col => visibleColumns.includes(col.id))
        .map((column, index) => {
          const isSticky = column.sticky === true
          const isCellEditing = editingCell?.rowId === row.id && editingCell?.columnId === column.id
          // The row's opening edge, so a selected row reads as a closed box
          // rather than two loose rules. Only the first cell draws it.
          const showLeftEdge = isSelected && index === 0
          // The select column's cell renders the row checkbox; it shares the
          // header's inset so the circles line up with the page title.
          const insetClass = selection && (column.id === 'select' || column.id === 'name') ? 'pl-checkbox-cell' : ''

          if (column.cell) {
            return (
              <DataTableBodyCell
                key={column.id}
                isSelected={isSelected}
                isSticky={isSticky}
                isScrolledX={isScrolledX}
                disableRowHover={disableRowHover}
                showLeftEdge={showLeftEdge}
                className={insetClass}
              >
                {column.cell(row)}
              </DataTableBodyCell>
            )
          }

          const value = column.accessor ? column.accessor(row) : null

          if (column.editable && onStartEdit && onSaveEdit && onCancelEdit) {
            return (
              <DataTableBodyCell
                key={column.id}
                isSelected={isSelected}
                isSticky={isSticky}
                isScrolledX={isScrolledX}
                disableRowHover={disableRowHover}
                showLeftEdge={showLeftEdge}
                className={`relative ${insetClass}`}
              >
                <DataTableEditableCell
                  value={value ?? null}
                  rowId={row.id}
                  columnId={column.id}
                  isEditing={isCellEditing}
                  onStartEdit={() => onStartEdit({ rowId: row.id, columnId: column.id })}
                  onSave={(newValue) => onSaveEdit(row.id, column.id, newValue)}
                  onCancel={onCancelEdit}
                />
              </DataTableBodyCell>
            )
          }

          return (
            <DataTableBodyCell
              key={column.id}
              isSelected={isSelected}
              isSticky={isSticky}
              isScrolledX={isScrolledX}
              disableRowHover={disableRowHover}
              showLeftEdge={showLeftEdge}
              className={insetClass}
            >
              <span
                className="text-content-body text-sm whitespace-nowrap overflow-hidden text-ellipsis block"
                title={value || undefined}
              >
                {value}
              </span>
            </DataTableBodyCell>
          )
        })}

      {rowActions && rowActions.length > 0 && (
        <td className={`px-4 py-2 relative transition-colors duration-0 ${cellBgClass}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="xs"
                className="h-10 w-10 p-0 has-[>svg]:px-0 shadow-xl transform-gpu"
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisHorizontalIcon className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {rowActions.map((action, index) => (
                <DropdownMenuItem
                  key={index}
                  className={action.variant === 'destructive' ? 'text-danger-foreground' : ''}
                  onClick={() => action.onClick(row)}
                >
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className={`absolute top-0 right-0 h-full ${isSelected ? 'bg-table-grid-selected' : 'bg-table-grid'}`} style={{ width: '1px' }} />
        </td>
      )}

      {hasSpacerColumn && (
        <td className={`relative transition-colors duration-0 ${cellBgClass}`}>
          {isSelected && (
            <div className="absolute top-0 right-0 h-full bg-table-grid-selected" style={{ width: '1px' }} />
          )}
        </td>
      )}
    </tr>
  )
}
